import {
  Component,
  signal,
  computed,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
  ElementRef,
  OnInit,
  inject,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GeographyService } from '../../../../../core/services/geography.service';
import { ProfessionalsService } from '../../../../../core/services/professionals.service';
import { UserSessionService } from '../../../../../core/services/mock-auth.service';
import { ProfessionalsWizardService } from '../../../../services/professionals-wizard.service';
import { ProfessionalsDataService } from '../../../../services/professionals-data.service';
import { convertEmptyStringsToNull } from '../../../../utils/form-data.utils';
import { Title } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { FileUploadModule } from 'primeng/fileupload';
import { ProfessionalDropdownOption, CollaboratorUI } from '../../../../../core/models/professional.models';
import { hasMeaningfulText } from '../../../../../core/validators/text-value.utils';
import { ValidationErrorSection, validationSectionsFromMessages } from '../../../validation-error-summary/validation-error-summary.model';
import { OperationalSitesSectionComponent } from '../operational-sites/operational-sites-section.component';
import {
  OperatingSiteVM,
  sitesFromResponse,
  sitesToRequest,
  validateOperationalSites,
} from '../operational-sites/operational-sites.model';

@Component({
  selector: 'app-doctor-form',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    CheckboxModule,
    RadioButtonModule,
    ToastModule,
    TooltipModule,
    FileUploadModule,
    OperationalSitesSectionComponent,
  ],
  templateUrl: './doctor-form.component.html',
  styleUrl: './shared-form-styles.scss',
})
export class DoctorFormComponent implements OnInit {
  @ViewChildren('firstInput') firstInputs!: QueryList<ElementRef>;

  readonly hasMeaningfulText = hasMeaningfulText;
  readonly today = new Date();

  // Optional professional ID from parent component (for review page)
  professionalIdInput = input<string | null>(null);

  @Output() onSave = new EventEmitter<any>();
  @Output() onSaveDraft = new EventEmitter<any>();
  @Output() validationFailed = new EventEmitter<ValidationErrorSection[]>();

  private geographyService = inject(GeographyService);
  private professionalsService = inject(ProfessionalsService);
  private mockAuthService = inject(UserSessionService);
  private titleService = inject(Title);
  private messageService = inject(MessageService);
  private professionalsWizardService = inject(ProfessionalsWizardService);
  private professionalsDataService = inject(ProfessionalsDataService);

  // Explicit readonly flag passed by parent
  readonly = input<boolean>(false);

  isReadOnly = computed(() => this.professionalsDataService.isReadOnly() || this.readonly());

  // Live region message for screen readers
  liveRegionMessage = signal<string>('');

  // Form submission tracking
  formSubmitted = signal<boolean>(false);
  public touchedFields = new Set<string>();

  // Section 1: Specific Information
  activityStartDate = signal<Date | null>(null);
  specialization = signal<string>('');
  professionalOrder = signal<string>('');
  orderRegistrationDate = signal<Date | null>(null);
  isPublicEmployee = signal<boolean>(false);
  isInailEmployee = signal<boolean>(false);
  assignedCompanies = signal<string>('');
  operatingTerritories = signal<string[]>([]);

  // Section 2: Sede operativa (multi-sede, gestita da app-operational-sites-section)
  operatingSameAsLegal = signal<boolean>(true);
  // Id della sede "legalFallback" persistita (round-trip per preservare i codici SOL associati)
  operatingLegalSiteId = signal<number | null>(null);
  operatingSites = signal<OperatingSiteVM[]>([]);

  // Section 3: Collaborators
  collaborators = signal<CollaboratorUI[]>([]);
  private collaboratorIdCounter = 0;

  // Dropdown options
  allProvincesOptions = signal<ProfessionalDropdownOption[]>([]); // For operating territories

  // Loading states
  isLoadingData = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  hadPreviousCollaborators = signal<boolean>(false);

  // Computed: Validation for Section 1
  section1Errors = computed(() => {
    const errors: string[] = [];

    if (this.formSubmitted() || this.touchedFields.has('activityStartDate')) {
      if (!this.activityStartDate()) {
        errors.push('Inizio Attività dal è obbligatorio');
      }
    }

    if (this.formSubmitted() || this.touchedFields.has('specialization')) {
      if (!this.specialization()?.trim()) {
        errors.push('Specializzazione è obbligatoria');
      }
    }

    if (this.formSubmitted() || this.touchedFields.has('professionalOrder')) {
      if (!this.professionalOrder()?.trim()) {
        errors.push('Ordine di è obbligatorio');
      }
    }

    if (this.formSubmitted() || this.touchedFields.has('orderRegistrationDate')) {
      if (!this.orderRegistrationDate()) {
        errors.push('Data di iscrizione all\'ordine è obbligatoria');
      }
    }

    return errors;
  });

  // Computed: Validation for Section 2 (sede operativa multi-sede)
  section2Errors = computed(() =>
    validateOperationalSites(this.operatingSameAsLegal(), this.operatingSites()),
  );

  // Computed: Validation for Section 3 (collaborators)
  section3Errors = computed(() => {
    const errors: string[] = [];
    const collabs = this.collaborators();

    collabs.forEach((collab, index) => {
      const prefix = `Collaboratore ${index + 1}:`;

      if (this.formSubmitted() || this.touchedFields.has(`collab-${collab.id}-lastName`)) {
        if (!collab.lastName?.trim()) {
          errors.push(`${prefix} Cognome è obbligatorio`);
        }
      }

      if (this.formSubmitted() || this.touchedFields.has(`collab-${collab.id}-firstName`)) {
        if (!collab.firstName?.trim()) {
          errors.push(`${prefix} Nome è obbligatorio`);
        }
      }

      if (this.formSubmitted() || this.touchedFields.has(`collab-${collab.id}-fiscalCode`)) {
        if (!collab.fiscalCode?.trim()) {
          errors.push(`${prefix} Codice Fiscale è obbligatorio`);
        } else if (!/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i.test(collab.fiscalCode)) {
          errors.push(`${prefix} Codice Fiscale non è valido`);
        }
      }

      if (this.formSubmitted() || this.touchedFields.has(`collab-${collab.id}-professionalOrder`)) {
        if (!collab.professionalOrder?.trim()) {
          errors.push(`${prefix} Ordine di è obbligatorio`);
        }
      }

      // Conditional validations for civil servants
      if (collab.civilServant) {
        if (this.formSubmitted() || this.touchedFields.has(`collab-${collab.id}-publicBody`)) {
          if (!collab.publicBody?.trim()) {
            errors.push(`${prefix} Ente pubblico è obbligatorio per dipendenti pubblici`);
          }
        }

        if (this.formSubmitted() || this.touchedFields.has(`collab-${collab.id}-communicationOffice`)) {
          if (!collab.communicationOffice?.trim()) {
            errors.push(`${prefix} Ufficio dell'ente è obbligatorio per dipendenti pubblici`);
          }
        }
      }
    });

    return errors;
  });

  // Computed: Overall form validity
  isFormValid = computed(() => {
    return (
      this.section1Errors().length === 0 &&
      this.section2Errors().length === 0
    );
  });

  ngOnInit(): void {
    this.titleService.setTitle('Portale Fiduciari - Dati Specifici Medico');
    this.loadDropdownOptions();
    this.loadExistingData();
  }

  private loadDropdownOptions(): void {
    // Load all provinces for operating territories
    this.geographyService
      .getProvinces()
      .pipe(catchError(() => of([])))
      .subscribe((provinces) => {
        this.allProvincesOptions.set(
          (provinces || []).map((province) => ({
            label: province.name,
            value: province.code,
          })),
        );
      });
  }

  markFieldAsTouched(fieldName: string): void {
    this.touchedFields.add(fieldName);
  }

  onPublicEmployeeChange(value: boolean): void {
    this.isPublicEmployee.set(value);
    this.professionalsWizardService.isPublicEmployeeDoctor.set(value);
    // Reset INAIL employee if public employee is unchecked
    if (!value) {
      this.isInailEmployee.set(false);
    }
  }

  onAssignedCompaniesChange(value: string): void {
    this.assignedCompanies.set(value);
  }

  createEmptyCollaborator(): CollaboratorUI {
    return {
      id: this.collaboratorIdCounter++,
      firstName: '',
      lastName: '',
      fiscalCode: '',
      professionalOrder: '',
      civilServant: false,
      publicBody: '',
      communicationOffice: '',
      claimsSystemAccess: false,
    };
  }

  addCollaborator(): void {
    const newCollab = this.createEmptyCollaborator();
    this.collaborators.update((current) => [...current, newCollab]);
    this.liveRegionMessage.set('Nuovo collaboratore aggiunto');

    setTimeout(() => {
      const inputs = this.firstInputs.toArray();
      if (inputs.length > 0) {
        inputs[inputs.length - 1].nativeElement.focus();
      }
      this.liveRegionMessage.set('');
    }, 100);
  }

  removeCollaborator(collaboratorId: number): void {
    this.collaborators.update((current) => current.filter((c) => c.id !== collaboratorId));
    this.liveRegionMessage.set('Collaboratore rimosso');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }



  private loadExistingData(): void {
    const professionalId = this.professionalIdInput() || this.mockAuthService.getDemoProfessionalId();

    if (!professionalId) {
      console.warn('⚠️ No professional ID found, cannot load doctor data');
      return;
    }

    // Check if data is already in cache (from wizard preload)
    const cachedData = this.professionalsWizardService.getCachedSpecificData();
    if (cachedData) {
      console.log('✅ Using cached doctor data (no API call)');
      this.populateFormFromData({ data: cachedData });
      return;
    }

    console.log('📥 Loading doctor data for professional:', professionalId);
    this.isLoadingData.set(true);

    this.professionalsService
      .getDoctorData(professionalId)
      .pipe(
        catchError((error: any) => {
          console.error('❌ Error loading doctor data:', error);
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Impossibile caricare i dati. Compila il form manualmente.',
            life: 5000,
          });
          return of(null);
        }),
      )
      .subscribe((response: any) => {
        this.isLoadingData.set(false);
        this.populateFormFromData(response);
      });
  }

  private populateFormFromData(response: any): void {
    if (!response || !response.data) {
      console.warn('⚠️ No doctor data returned from API');
      // Mark step as NOT completed when no data exists
      this.professionalsWizardService.markStepCompleted(1, false);
      this.professionalsWizardService.saveState();
      return;
    }

    const data = response.data;
    console.log('✅ Doctor data loaded:', data);

        // Populate Section 1
        if (data.specificData) {
          if (data.specificData.activityStartDate) {
            this.activityStartDate.set(new Date(data.specificData.activityStartDate));
          }
          if (data.specificData.specialization) {
            this.specialization.set(data.specificData.specialization);
          }
          if (data.specificData.professionalOrder) {
            this.professionalOrder.set(data.specificData.professionalOrder);
          }
          if (data.specificData.orderRegistrationDate) {
            this.orderRegistrationDate.set(new Date(data.specificData.orderRegistrationDate));
          }

          if (data.specificData.isPublicEmployee !== undefined) {
            this.isPublicEmployee.set(data.specificData.isPublicEmployee);
            this.professionalsWizardService.isPublicEmployeeDoctor.set(data.specificData.isPublicEmployee);
          }
          if (data.specificData.isInailEmployee !== undefined) {
            this.isInailEmployee.set(data.specificData.isInailEmployee);
          }
          if (data.specificData.assignedCompanies) {
            this.assignedCompanies.set(data.specificData.assignedCompanies);
          }
          if (data.specificData.operatingTerritories && Array.isArray(data.specificData.operatingTerritories)) {
            this.operatingTerritories.set(data.specificData.operatingTerritories);
          }
        }

        // Populate Section 2: Sede operativa (backend: operationalSites[] multi-sede con flag legalFallback)
        const loadedSites = sitesFromResponse(data.specificData?.operationalSites);
        this.operatingSameAsLegal.set(loadedSites.sameAsLegal);
        this.operatingLegalSiteId.set(loadedSites.legalSiteId);
        this.operatingSites.set(loadedSites.sites);

        // Populate Section 3: Collaborators
        if (data.collaborators && Array.isArray(data.collaborators)) {
          const loadedCollaborators = data.collaborators.map((collab: any) => ({
            id: this.collaboratorIdCounter++,
            firstName: collab.firstName || '',
            lastName: collab.lastName || '',
            fiscalCode: collab.fiscalCode || '',
            professionalOrder: collab.professionalOrder || '',
            civilServant: collab.civilServant || false,
            publicBody: collab.publicBody || '',
            communicationOffice: collab.communicationOffice || '',
            claimsSystemAccess: collab.needsClaimsPortalAccess || false,
          }));
          this.collaborators.set(loadedCollaborators);
          this.hadPreviousCollaborators.set(true);
        }

        // Check section status and mark step as completed ONLY if FILLED
        if (data.specificData?.specificDataSectionStatus === 'FILLED') {
          console.log('✅ Specific data section is FILLED - marking step as completed');
          this.professionalsWizardService.markStepCompleted(1, true);
        } else {
          console.log('⚠️ Specific data section is NOT filled (status:', data.specificData?.specificDataSectionStatus, ') - marking step as NOT completed');
          this.professionalsWizardService.markStepCompleted(1, false);
        }
        
        // Save wizard state to persist the completed status
        this.professionalsWizardService.saveState();
  }

  validateForm(): boolean {
    this.formSubmitted.set(true);

    // Mark all fields as touched
    this.touchedFields.add('activityStartDate');
    this.touchedFields.add('specialization');
    this.touchedFields.add('professionalOrder');
    this.touchedFields.add('orderRegistrationDate');
    this.touchedFields.add('assignedCompanies');

    this.collaborators().forEach((collab) => {
      this.touchedFields.add(`collab-${collab.id}-lastName`);
      this.touchedFields.add(`collab-${collab.id}-firstName`);
      this.touchedFields.add(`collab-${collab.id}-fiscalCode`);
      this.touchedFields.add(`collab-${collab.id}-professionalOrder`);
      if (collab.civilServant) {
        this.touchedFields.add(`collab-${collab.id}-publicBody`);
        this.touchedFields.add(`collab-${collab.id}-communicationOffice`);
      }
    });

    const isValid = this.isFormValid();

    if (!isValid) {
      this.validationFailed.emit(validationSectionsFromMessages([
        { title: 'Informazioni specifiche', errors: this.section1Errors() },
        { title: 'Sede operativa', errors: this.section2Errors() },
      ]));
      const allErrors = [
        ...this.section1Errors(),
        ...this.section2Errors(),
      ];

      this.liveRegionMessage.set(`Form non valido: ${allErrors.length} errori trovati`);
      setTimeout(() => this.liveRegionMessage.set(''), 3000);
    }

    return isValid;
  }

  buildPayload(): any {
    const payload: any = {
      specificData: {
        activityStartDate: this.activityStartDate()?.toISOString().split('T')[0] || null,
        specialization: this.specialization() || null,
        professionalOrder: this.professionalOrder() || null,
        orderRegistrationDate: this.orderRegistrationDate()?.toISOString().split('T')[0] || null,
        isPublicEmployee: this.isPublicEmployee(),
        isInailEmployee: this.isInailEmployee(),
        operatingTerritories: this.operatingTerritories(),
        assignedCompanies: this.assignedCompanies() || null,
      },
    };

    // Sede operativa (backend: operationalSites[] multi-sede con flag legalFallback)
    payload.specificData.operationalSites = sitesToRequest(
      this.operatingSameAsLegal(),
      this.operatingLegalSiteId(),
      this.operatingSites(),
    );

    // I collaboratori sono ora gestiti nella sezione dedicata "Impiegati Amministrativi e Collaboratori" del wizard.

    console.log('📤 Doctor form payload:', payload);

    return payload;
  }

  saveDraft(): void {
    console.log('💾 Saving doctor draft...');

    const professionalId = this.mockAuthService.getDemoProfessionalId();

    if (!professionalId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID Fiduciario non trovato',
        life: 5000,
      });
      return;
    }

    this.isSaving.set(true);
    const payload = this.buildPayload();

    // Add section status to specificData
    payload.specificData.specificDataSectionStatus = 'DRAFT';

    this.professionalsService.updateDoctorData(professionalId, payload).subscribe({
      next: (response: any) => {
        this.isSaving.set(false);
        console.log('✅ Draft saved successfully:', response);

        this.messageService.add({
          severity: 'success',
          summary: 'Bozza Salvata',
          detail: 'I dati sono stati salvati come bozza',
          life: 5000,
        });

        this.liveRegionMessage.set('Bozza salvata con successo');
        setTimeout(() => this.liveRegionMessage.set(''), 3000);

        this.onSaveDraft.emit(convertEmptyStringsToNull(payload));
      },
      error: (error: any) => {
        this.isSaving.set(false);
        console.error('❌ Error saving draft:', error);

        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile salvare la bozza',
          life: 5000,
        });
      },
    });
  }

  saveAndContinue(): void {
    console.log('💾 Saving doctor form and continuing...');

    if (!this.validateForm()) {
      return;
    }

    const professionalId = this.mockAuthService.getDemoProfessionalId();

    if (!professionalId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID Fiduciario non trovato',
        life: 5000,
      });
      return;
    }

    this.isSaving.set(true);
    const payload = this.buildPayload();

    // Add section status to specificData
    payload.specificData.specificDataSectionStatus = 'FILLED';

    this.professionalsService.updateDoctorData(professionalId, payload).subscribe({
      next: (response: any) => {
        this.isSaving.set(false);
        console.log('✅ Doctor data saved successfully:', response);

        this.messageService.add({
          severity: 'success',
          summary: 'Salvato',
          detail: 'Dati salvati con successo',
          life: 5000,
        });

        this.liveRegionMessage.set('Dati salvati con successo');

        // Emit save event
        this.onSave.emit(payload);
      },
      error: (error: any) => {
        this.isSaving.set(false);
        console.error('❌ Error saving doctor data:', error);

        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Impossibile salvare i dati',
          life: 5000,
        });
      },
    });
  }
}
